import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { collection, doc, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useCollection, useDocument } from '@/hooks/firestore';
import { Card, EmptyState, LoadingSkeleton } from '@/components/ui/Card';
import { RatingStars } from '@/components/ui/RatingStars';
import { SellerSafetyBadges } from '@/components/seller/SellerSafetyBadges';
import { ProfileReel } from '@/components/seller/ProfileReel';
import { sellerReel } from '@/lib/sellerReel';
import {
  CATEGORY_LABELS,
  formatZAR,
  type PublicProfileDoc,
  type ReviewDoc,
  type ServiceDoc,
} from '@makhe/shared';

export default function SellerProfilePage() {
  const { sellerId } = useParams();
  const userRef = sellerId ? doc(db, 'publicProfiles', sellerId) : null;
  const { data: seller, loading, error } = useDocument<PublicProfileDoc>(userRef);

  const servicesQ = useMemo(() => {
    if (!sellerId) return null;
    return query(
      collection(db, 'services'),
      where('status', '==', 'active'),
      where('sellerId', '==', sellerId),
      orderBy('createdAt', 'desc'),
      limit(20),
    );
  }, [sellerId]);
  const { data: services } = useCollection<ServiceDoc>(servicesQ);

  const reviewsQ = useMemo(() => {
    if (!sellerId) return null;
    return query(
      collection(db, 'reviews'),
      where('sellerId', '==', sellerId),
      where('isHidden', '==', false),
      orderBy('createdAt', 'desc'),
      limit(20),
    );
  }, [sellerId]);
  const { data: reviews } = useCollection<ReviewDoc>(reviewsQ);

  if (loading) return <LoadingSkeleton className="m-6 h-40" />;
  if (error)
    return (
      <div className="p-6">
        <EmptyState
          title="Couldn’t load this profile"
          description="Something went wrong. Check your connection and try again."
        />
      </div>
    );
  if (!seller || !seller.roles.seller)
    return (
      <div className="p-6">
        <EmptyState title="Creator not found" description="This profile isn’t available." />
      </div>
    );

  const profile = seller.sellerProfile;

  return (
    <section className="mx-auto max-w-3xl px-6 py-6">
      <header className="flex items-center gap-4">
        <div className="h-20 w-20 overflow-hidden rounded-full bg-makhe-line/50">
          {seller.photoURL ? (
            <img src={seller.photoURL} alt="" className="h-full w-full object-cover" />
          ) : null}
        </div>
        <div>
          <h1 className="font-display text-2xl">{seller.displayName}</h1>
          <p className="text-sm text-makhe-muted">
            {profile?.categories.map((c) => CATEGORY_LABELS[c]).join(' · ')}
          </p>
          <div className="mt-1 flex items-center gap-2">
            <RatingStars value={profile?.averageRating ?? 0} size="sm" />
            <span className="text-xs text-makhe-muted">
              {(profile?.averageRating ?? 0).toFixed(1)} ({profile?.totalReviews ?? 0})
            </span>
          </div>
        </div>
      </header>

      {seller.bio && <p className="mt-6 text-makhe-ink/80">{seller.bio}</p>}
      <SellerSafetyBadges className="mt-3" preferences={profile?.safetyPreferences} />

      <ProfileReel clips={sellerReel(profile?.galleryVideos, services)} />

      <h2 className="mt-8 font-display text-xl">Services</h2>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        {services.length === 0 && <EmptyState title="No live services right now" />}
        {services.map((s) => (
          <Link key={s.id} to={`/service/${s.id}`}>
            <Card className="overflow-hidden p-0">
              <div className="aspect-video w-full bg-black">
                {s.media.videoThumbnailURL ? (
                  <img
                    src={s.media.videoThumbnailURL}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : null}
              </div>
              <div className="p-3">
                <p className="font-medium">{s.title}</p>
                <p className="text-xs text-makhe-muted">
                  from {formatZAR(Math.min(...s.pricingTiers.map((t) => t.priceZARcents)))}
                </p>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      <h2 className="mt-10 font-display text-xl">Reviews</h2>
      <div className="mt-3 space-y-3">
        {reviews.length === 0 && <EmptyState title="No reviews yet" />}
        {reviews.map((r) => (
          <Card key={r.id}>
            <RatingStars value={r.rating} size="sm" />
            {r.textReview && <p className="mt-2 text-sm text-makhe-ink/80">{r.textReview}</p>}
            {r.videoReviewURL && (
              <video src={r.videoReviewURL} controls className="mt-3 max-h-72 rounded-xl" />
            )}
            {r.photoURLs?.length > 0 && (
              <div className="mt-3 grid grid-cols-3 gap-2">
                {r.photoURLs.map((url) => (
                  <a key={url} href={url} target="_blank" rel="noreferrer" className="aspect-square overflow-hidden rounded-lg bg-makhe-line/40">
                    <img src={url} alt="Review photo" loading="lazy" className="h-full w-full object-cover" />
                  </a>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>
    </section>
  );
}
